import ky from 'ky';
import { apiUrl, apiTimeout } from '@/config';
import { ApiErrorMessage } from '@/types/errors';
import { AuthTokens } from './mutations/auth';
import {
  getAccessToken,
  getIdToken,
  setAccessToken,
  setIdToken,
} from '@/lib/tokenManager';
import { isCognitoTokenExpired } from './utils';

let isRefreshing = false;
let failedQueue: {
  resolve: (value: string | null) => void;
  reject: (reason?: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    }
    else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Callback function that will be called when new tokens are received
let onNewTokensCallback: ((tokens: AuthTokens | null) => void) | null = null;
export const setOnNewTokensCallback = (
  callback: typeof onNewTokensCallback,
) => {
  onNewTokensCallback = callback;
};

const api = ky.extend({
  prefixUrl: apiUrl,
  timeout: apiTimeout ?? 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        let idToken = getIdToken();
        const accessToken = getAccessToken();

        // Skip expiration check for refresh-token and logout endpoints
        if (request.url.includes('/refresh-token') || request.url.includes('/logout')) {
          request.headers.set(
            'Authorization',
            `Bearer ${request.url.includes('/logout') ? accessToken : idToken}`,
          );
          return;
        }

        // Token expired try to refresh it
        if (idToken && isCognitoTokenExpired(idToken)) {
          // if another request is already refreshing wait for it
          if (isRefreshing) {
            idToken = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
          }
          else {
            isRefreshing = true;

            try {
              const response = await ky
                .post(`${apiUrl}/auth/refresh-token`, {
                  credentials: 'include',
                })
                .json<{ tokens: AuthTokens }>();

              // Validate new idToken
              const newIdToken = response.tokens.idToken;
              if (!newIdToken || isCognitoTokenExpired(newIdToken)) {
                throw new Error('Received invalid or expired idToken from refresh-token endpoint');
              }
              // Update global accessToken and idToken
              idToken = newIdToken;
              setAccessToken(response.tokens.accessToken);
              setIdToken(idToken);

              if (onNewTokensCallback) {
                onNewTokensCallback(response.tokens);
              }
              processQueue(null, idToken);
            }
            catch (error) {
              console.error('Failed to refresh token:', error);

              idToken = null;
              setAccessToken(null);
              setIdToken(idToken);
              if (onNewTokensCallback) {
                onNewTokensCallback(null);
              }

              processQueue(error, null);
              throw error;
            }
            finally {
              isRefreshing = false;
            }
          }
        }

        // Attach idToken to the request auth header
        if (idToken) {
          request.headers.set('Authorization', `Bearer ${idToken}`);
        }
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error;
        const apiError: { error?: ApiErrorMessage } = await response.json();

        error.message = apiError.error ?? error.message;
        return error;
      },
    ],
  },
});

// Separate instance for potentially long-running uploads (e.g. large image files)
export const uploadApi = api.extend({
  timeout: 60_000, // 60 seconds for uploads
});

export default api;
